import { useEffect, useState, useCallback } from 'react'
import type { Page } from '../api/client'
import { paymentBatchesApi, type PaymentBatch } from '../api/finance-ext'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import {
  AccessDenied,
  Callout,
  DataTable,
  DataTableEmptyRow,
  FilterBar,
  Icon,
  IconButton,
  LoadingState,
  Page as PageLayout,
  PageHeader,
  Pagination,
  Select,
  StatCard,
  StatGrid,
  StatusPill,
  type PillTone,
  useConfirm,
  usePrompt,
} from '../components/ui'

const STATUS_COLORS: Record<string, PillTone> = {
  draft: 'warning',
  confirmed: 'success',
  rejected: 'error',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  confirmed: 'Confermato',
  rejected: 'Rifiutato',
}

const PAGE_SIZE = 25

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Renders a redacted-amount cell: a lock icon with a tooltip when the backend
 * omitted the field (staff without CanSeeRealAmounts), otherwise the normal
 * formatted amount. */
function AmountCell({ value }: { value: number | undefined }) {
  if (value === undefined) {
    return (
      <span className="text-muted" title="Importo non visibile">
        <Icon name="lock" size={18} />
      </span>
    )
  }
  return <>{formatEur(value)}</>
}

/** F-101: ordinamento di default di GET /api/payment-batches (creazione decrescente). */
const DEFAULT_SORT = 'created_desc'

export function PaymentsPage() {
  const confirm = useConfirm()
  const prompt = usePrompt()
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Page<PaymentBatch> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      const p: { page: number; status?: string; sort: string } = { page, sort }
      if (status) p.status = status
      const result = await paymentBatchesApi.list(p)
      setData(result)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore'
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')) {
        setUnauthorized(true)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [status, page, sort])

  useEffect(() => {
    void load()
  }, [load])

  function handleSort(next: string) { setSort(next); setPage(1) }

  async function handleConfirm(id: number): Promise<void> {
    const reason = await prompt({
      title: 'Conferma il lotto di pagamenti',
      label: 'Motivo della conferma (se richiesto)',
      confirmLabel: 'Conferma',
    })
    if (reason === null) return
    try {
      await paymentBatchesApi.confirm(id, reason.trim() || undefined)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore conferma')
    }
  }

  async function handleDelete(id: number): Promise<void> {
    if (!(await confirm({ title: 'Elimina batch di pagamento', message: 'Eliminare definitivamente questo batch di pagamento?', confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await paymentBatchesApi.delete(id)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione')
    }
  }

  async function handleReject(id: number): Promise<void> {
    const reason = await prompt({
      title: 'Rifiuta il lotto di pagamenti',
      label: 'Motivo del rifiuto',
      required: true,
      confirmLabel: 'Rifiuta',
    })
    if (reason === null) return
    const trimmed = reason.trim()
    if (!trimmed) return
    try {
      await paymentBatchesApi.reject(id, trimmed)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore rifiuto')
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const confirmedCount = items.filter(b => b.status === 'confirmed').length
  const totalAmount = items.reduce((sum, b) => sum + (b.totalAmount ?? 0), 0)

  return (
    <PageLayout>
      <PageHeader
        title="Pagamenti"
        icon="credit_card"
        {...(!unauthorized ? { count: total } : {})}
      />

      <FilterBar>
        {!unauthorized && (
          <Select
            label="Stato"
            value={status}
            onChange={e => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Tutti</option>
            <option value="draft">Bozze</option>
            <option value="confirmed">Confermati</option>
            <option value="rejected">Rifiutati</option>
          </Select>
        )}
        <SortResetLink sort={sort} defaultSort={DEFAULT_SORT} onReset={() => handleSort(DEFAULT_SORT)} />
      </FilterBar>

      {unauthorized && <AccessDenied requirement="il ruolo EqualStaff" />}

      {error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <>
          <StatGrid>
            <StatCard label="Totale batch" value={total} />
            <StatCard label="Confermati" supporting="pagina corrente" value={confirmedCount} tone="success" />
            <StatCard label="Importo totale" supporting="pagina corrente" value={formatEur(totalAmount)} />
          </StatGrid>

          {loading ? (
            <LoadingState />
          ) : (
                <DataTable
                  footer={Math.ceil(total / PAGE_SIZE) > 1 ? (
                    <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
                  ) : undefined}
                >
                  <thead>
                    <tr>
                      <SortKeyTh label="Codice" ascKey="created_asc" descKey="created_desc" sort={sort} onSort={handleSort} title="Ordina per data di creazione" />
                      <th>Data Pagamento</th>
                      <SortKeyTh label="Stato" ascKey="status" sort={sort} onSort={handleSort} />
                      <th className="md-num">Importo Totale</th>
                      <th className="md-num">Importo Netto</th>
                      <th className="text-end">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && <DataTableEmptyRow colSpan={6} icon="payments" title="Nessun pagamento trovato" />}
                    {items.map(b => (
                      <tr key={b.id}>
                        <td className="font-monospace md-typescale-emphasized-label-large">{b.batchCode}</td>
                        <td>{formatDate(b.paymentDate)}</td>
                        <td>
                          <StatusPill tone={STATUS_COLORS[b.status] ?? 'neutral'}>
                            {STATUS_LABELS[b.status] ?? b.status}
                          </StatusPill>
                        </td>
                        <td className="md-num"><AmountCell value={b.totalAmount} /></td>
                        <td className="md-num"><AmountCell value={b.netAmount} /></td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-1">
                            {b.status === 'draft' && (
                              <IconButton
                                icon="check_circle"
                                size="sm"
                                aria-label="Conferma"
                                onClick={() => void handleConfirm(b.id)}
                              />
                            )}
                            {b.status === 'draft' && (
                              <IconButton
                                icon="cancel"
                                size="sm"
                                aria-label="Rifiuta"
                                onClick={() => void handleReject(b.id)}
                              />
                            )}
                            {b.status === 'draft' && (
                              <IconButton
                                icon="delete"
                                size="sm"
                                aria-label="Elimina"
                                onClick={() => void handleDelete(b.id)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
          )}
        </>
      )}
    </PageLayout>
  )
}
